// The process modal's log: the output of one background process, kept in seq order and capped at
// the last PROCESS_LOG_CAP lines (oldest dropped). Fed by the daemon's backlog read
// (cyborg:tail_agent_process) and its live frames (cyborg:agent_process_output); seq is the
// 1-based line number in the process's whole output, so both feeds merge without duplicates.

export const PROCESS_LOG_CAP = 10_000;

// cyborg:tail_agent_process_response, the fields the log uses.
export interface AgentProcessTail {
  lines: string[];
  firstSeq: number;
  lastSeq: number;
  partial: string;
  more: boolean;
  droppedBefore: boolean;
  running: boolean;
  status: "running" | "completed" | "failed" | "stopped";
  exitCode: number | null;
}

// cyborg:agent_process_output. `firstSeq`, not `seq`: the relay stamps payload.seq with its own
// workspace sequence. privateToEmail is relay routing, never rendered.
export interface AgentProcessOutputFrame {
  workspaceId: string;
  agentId: string;
  processId: string;
  firstSeq: number;
  lines: string[];
  partial: string;
  privateToEmail?: string;
}

export type ProcessLogError = "forbidden" | "not_found" | "invalid" | "failed";

export interface ProcessLog {
  lines: string[];
  // The unterminated last line (a progress bar): drawn after `lines`, replaced on every update.
  partial: string;
  lastSeq: number;
  // Earlier output exists that this log does not hold (trimmed by the daemon or by the cap).
  droppedBefore: boolean;
  // The daemon refused or failed the read: "forbidden" is the not-the-owner answer.
  error: ProcessLogError | null;
}

const key = (agentId: string, processId: string): string => `${agentId}\t${processId}`;

export class AgentProcessOutputStore {
  private logs = $state<Record<string, ProcessLog>>({});

  get(agentId: string, processId: string): ProcessLog | undefined {
    return this.logs[key(agentId, processId)];
  }

  // A backlog page. Without a log yet it starts one; otherwise only lines past lastSeq are added.
  applyTail(agentId: string, processId: string, tail: AgentProcessTail): void {
    const k = key(agentId, processId);
    const cur = this.logs[k] ?? {
      lines: [],
      partial: "",
      lastSeq: 0,
      droppedBefore: false,
      error: null,
    };
    this.logs[k] = append(cur, tail.firstSeq, tail.lines, tail.partial, tail.droppedBefore);
  }

  // A re-read from the start: the backlog replaces the log instead of extending it. Codex learns a
  // process's first block only once it ends, and the daemon rebuilds the whole output under new
  // seqs; droppedBefore stays the daemon's (true only when lines were cut for its cap).
  replaceTail(agentId: string, processId: string, tail: AgentProcessTail): void {
    const empty = { lines: [], partial: "", lastSeq: 0, droppedBefore: false, error: null };
    this.logs[key(agentId, processId)] = append(
      empty,
      tail.firstSeq,
      tail.lines,
      tail.partial,
      tail.droppedBefore,
    );
  }

  // A live frame. false: it skips lines this log never saw (a hole), so the caller re-reads the
  // backlog from lastSeq + 1. A frame for a process nobody follows is ignored.
  applyFrame(frame: AgentProcessOutputFrame): boolean {
    const k = key(frame.agentId, frame.processId);
    const cur = this.logs[k];
    if (!cur) return true;
    if (frame.firstSeq > cur.lastSeq + 1) return false;
    this.logs[k] = append(cur, frame.firstSeq, frame.lines, frame.partial, false);
    return true;
  }

  fail(agentId: string, processId: string, error: ProcessLogError): void {
    const k = key(agentId, processId);
    const cur = this.logs[k] ?? { lines: [], partial: "", lastSeq: 0, droppedBefore: false, error };
    this.logs[k] = { ...cur, error };
  }

  forget(agentId: string, processId: string): void {
    delete this.logs[key(agentId, processId)];
  }

  clearAll(): void {
    this.logs = {};
  }
}

function append(
  cur: ProcessLog,
  firstSeq: number,
  lines: string[],
  partial: string,
  droppedBefore: boolean,
): ProcessLog {
  const fresh = lines.slice(Math.max(0, cur.lastSeq + 1 - firstSeq));
  const log: ProcessLog = {
    lines: fresh.length ? [...cur.lines, ...fresh] : cur.lines,
    partial,
    lastSeq: fresh.length ? firstSeq + lines.length - 1 : cur.lastSeq,
    droppedBefore: cur.droppedBefore || droppedBefore,
    error: null,
  };
  if (log.lines.length <= PROCESS_LOG_CAP) return log;
  return { ...log, lines: log.lines.slice(-PROCESS_LOG_CAP), droppedBefore: true };
}

// The one instance. app.svelte.ts feeds it and clears it on logout.
export const agentProcessOutputStore = new AgentProcessOutputStore();
